import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MapPin, X } from 'lucide-react';

/**
 * VenueLookup — "type your club, pick the match" address picker (W-2 / W-4).
 *
 * Used by the club wizard (compulsory address → doubles as Home Ground) and
 * the verification form. Talks to GET /geocode?mode=venue, which is
 * sports-club-biased: "Clevedon RFC" surfaces Clevedon's rugby club, not a
 * KFC. The caller only ever receives a PICKED candidate (with coords) —
 * free text is never a value — so what's stored is always a real place.
 *
 * value:   null | { label, line, lat, lng, locality, region, kind }
 * onChange(value)
 */
const VenueLookup = ({ value, onChange, placeholder, autoFocus, inputId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState('');
  const debounce = useRef(null);

  useEffect(() => () => clearTimeout(debounce.current), []);

  const search = (val) => {
    setQuery(val);
    setHint('');
    clearTimeout(debounce.current);
    if (val.trim().length < 3) { setResults([]); setOpen(false); return; }
    setLoading(true);
    debounce.current = setTimeout(async () => {
      try {
        const r = await axios.get('/geocode', { params: { q: val.trim(), limit: 6, mode: 'venue' } });
        const list = Array.isArray(r.data) ? r.data : [];
        setResults(list);
        setOpen(true);
        if (!list.length) setHint('Nothing matched — try the club name plus town, the postcode, or the street address.');
      } catch {
        setHint('Search failed — check your connection and try again.');
      } finally { setLoading(false); }
    }, 300);
  };

  const pick = (c) => {
    onChange({
      // Only a real venue carries a name; for an address/postcode the label
      // stays empty and the server falls back to the club's name, so a
      // ground is never called "BS21 6PT" (marakin-qa, slice 1).
      label: c.kind === 'poi' ? (c.name || (c.displayName || '').split(',')[0] || '') : '',
      line: c.displayName || '',
      lat: c.lat,
      lng: c.lng,
      locality: c.locality || c.address?.city || c.address?.town || null,
      region: c.region || c.address?.county || null,
      kind: c.kind || 'address',
    });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  if (value) {
    return (
      <div className="venue-picked">
        <MapPin size={16} />
        <div className="venue-picked-text">
          {value.label && value.label !== value.line && <strong>{value.label}</strong>}
          <span>{value.line}</span>
        </div>
        <button type="button" className="venue-picked-clear" aria-label="Change address" onClick={() => onChange(null)}>
          <X size={14} /> Change
        </button>
      </div>
    );
  }

  return (
    <div className="venue-lookup">
      <input
        id={inputId}
        className="input"
        value={query}
        onChange={e => search(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder || 'e.g. Clevedon RFC, or BS21 6PT'}
        autoComplete="off"
        autoFocus={autoFocus}
      />
      {loading && <span className="venue-lookup-loading">Searching…</span>}
      {open && results.length > 0 && (
        <div className="venue-lookup-results" role="listbox">
          {results.map((c, i) => {
            const isPOI = c.kind === 'poi';
            const isPostcode = c.kind === 'postcode_area';
            return (
              <button key={`${c.lat}-${c.lng}-${i}`} type="button" role="option" aria-selected="false"
                className="venue-lookup-result" onMouseDown={() => pick(c)}>
                <span className={`venue-kind ${isPOI ? 'poi' : isPostcode ? 'postcode' : 'address'}`}>
                  {isPOI ? 'VENUE' : isPostcode ? 'POSTCODE' : 'ADDRESS'}
                </span>
                <span className="venue-result-text">
                  {c.name && c.name !== c.displayName && <strong>{c.name}</strong>}
                  <span>{c.displayName}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {hint && <p className="venue-lookup-hint">{hint}</p>}
    </div>
  );
};

export default VenueLookup;
